import React, { useEffect, useState } from "react";
import { 
  useGetEntraSecurityQuery, 
  useAddEntraSecurityMutation, 
  useUpdateEntraSecurityMutation, 
  useDeleteEntraSecurityMutation 
} from "../../../services/ApiSlice.ts";
import EntraSecurityEntry from './EntraEntry.tsx';
import { EntraSecurityAdminDto } from "../../../services/ApiTypes.ts";
import { Modal, Button, List } from "antd";
import CustomSpinner from "../../core/spinner/CustomSpinner.tsx";

const emptyGuid = "00000000-0000-0000-0000-000000000000"

interface EntraSecurityModalProps {
  visible: boolean;
  selectedEntraSecurity: EntraSecurityAdminDto | null;
  onClose: () => void;
  onSelect: (selectedEntry: EntraSecurityAdminDto) => void;
}

const EntraSecurityModal: React.FC<EntraSecurityModalProps> = ({
  visible,
  selectedEntraSecurity,
  onClose,
  onSelect,
}) => {
  const { data, isLoading: isFetchingData } = useGetEntraSecurityQuery(null);
  const [entries, setEntries] = useState<EntraSecurityAdminDto[]>([]);
  const [addEntraSecurity, { isLoading: isAdding }] = useAddEntraSecurityMutation();
  const [updateEntraSecurity, { isLoading: isUpdating }] = useUpdateEntraSecurityMutation();
  const [deleteEntraSecurity, { isLoading: isDeleting }] = useDeleteEntraSecurityMutation();

  const [tempSelectedEntra, setTempSelectedEntra] = useState<string | null>(selectedEntraSecurity ? selectedEntraSecurity.id : null);

  const [selectedEntra, setSelectedEntra] = useState<string | null>(selectedEntraSecurity ? selectedEntraSecurity.id : null);
  const [newEntry, setNewEntry] = useState<EntraSecurityAdminDto | null>(null);
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const isLoading = isFetchingData || isAdding || isUpdating || isDeleting;

  useEffect(() => {
    if (visible) {
      setTempSelectedEntra(selectedEntraSecurity ? selectedEntraSecurity.id : null);
    }
  }, [selectedEntraSecurity, visible]);

  useEffect(() => {
    setSelectedEntra(selectedEntraSecurity ? selectedEntraSecurity.id : null);
  }, [selectedEntraSecurity]);

  useEffect(() => {
    if (data !== undefined) {
      setEntries(data);
    }
  }, [data]);

  const handleAddButtonClick = () => {
    const newEntryTemplate: EntraSecurityAdminDto = {
      id: null,
      name: "",
      tenantId: "",
      clientId: "",
    };
    setNewEntry(newEntryTemplate);
  };

  const handleSave = async (entry: EntraSecurityAdminDto) => {
    if (entry.id) {
      await updateEntraSecurity({ entraSecurityId: entry.id, entraSecurity: entry });
    } else {
      let newEntry: EntraSecurityAdminDto = { ...entry, id: emptyGuid };
      await addEntraSecurity({ entraSecurity: newEntry });
      setNewEntry(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (id === "") {
      setNewEntry(null);
    } else {
      try {
        await deleteEntraSecurity({ entraSecurityId: id }).unwrap();
      } catch (error: any) {
        console.log(error);
        const errorMessage =
          error?.data?.Message;
        if (!!errorMessage && errorMessage.startsWith("Cannot delete Entra Group")){
          setErrorMessage(errorMessage);
        } else{
          setErrorMessage("An error occurred while saving the entry.");
        }
        setErrorModalVisible(true);
      }
    }
  };

  const handleSelect = (entry: EntraSecurityAdminDto) => {
    setTempSelectedEntra(entry.id);
  };

  const handleSaveClick = () => {
    const selectedEntry = entries.find(e => e.id === tempSelectedEntra) ?? null;
    if (selectedEntry) {
      onSelect(selectedEntry);
    }
    onClose();
  };

  const handleClose = () => {
    setNewEntry(null);
    onClose();
  };

  return (
    <>
      <Modal
        title="Manage Azure Entra"
        className="custom-modal"
        open={visible}
        width={"700px"}
        onClose={handleClose}
        onCancel={handleClose}
        footer={[
          <Button key="ok" type="primary" onClick={handleSaveClick} data-testid="okButton">
            Apply
          </Button>,
        ]}
      >
        {isLoading ? (
          <CustomSpinner />
        ) : (
          <div>
            <List
              dataSource={newEntry ? [...entries, newEntry] : [...entries]}
              renderItem={(entry) => (
                <List.Item key={entry.id ?? `temp-${Math.random()}`}>
                  <EntraSecurityEntry
                    entraSecurityEntry={entry}
                    isSelected={entry.id === tempSelectedEntra}
                    onSave={handleSave}
                    onDelete={() => handleDelete(entry.id ?? "")}
                    onSelect={() => handleSelect(entry)}
                  />
                </List.Item>
              )}
            />
            <Button
              type="primary"
              onClick={handleAddButtonClick}
              data-testid="addButton"
            >
              New Group
            </Button>
          </div>
        )}
      </Modal>
      <Modal
        title="Error"
        open={errorModalVisible}
        onCancel={() => setErrorModalVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setErrorModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        <p>{errorMessage}</p>
      </Modal>
    </>
  );
};

export default EntraSecurityModal;
